import classNames from 'classnames';
import { useId, type ReactNode } from 'react';
import styles from './UgPager.module.css';

/**
 * Center page numbers (excluding the leading `1` and optional trailing last page).
 * e.g. total 76: page 1 → `[2,3]` · pages 2–3 → `[2,3,4]` · page 76 → `[74,75,76]`.
 */
export function getUgPagerWindowPages(
  currentPage: number,
  totalPages: number
): number[] {
  const t = Math.max(1, totalPages);
  const c = Math.min(Math.max(1, currentPage), t);

  if (t <= 4) {
    return Array.from({ length: t }, (_, i) => i + 1);
  }
  if (c === 1) {
    return [2, 3].filter((p) => p <= t);
  }
  if (c === 2 || c === 3) {
    return [2, 3, 4].filter((p) => p <= t);
  }
  if (c === t) {
    return [t - 2, t - 1, t].filter((p) => p >= 1);
  }
  if (c === t - 1 || c === t - 2) {
    return [t - 3, t - 2, t - 1].filter((p) => p >= 1);
  }
  return [c - 1, c, c + 1].filter((p) => p >= 1 && p <= t);
}

/** @deprecated use {@link getUgPagerWindowPages} — kept for callers that passed `visiblePages` manually */
export function getUgPagerVisiblePages(
  currentPage: number,
  totalPages: number
): number[] {
  return getUgPagerWindowPages(currentPage, totalPages);
}

export interface UgPagerProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** If set, overrides center window (1-based page indices); first/last/chevrons still apply when total > 4 */
  visiblePages?: number[];
}

function pageButton(
  baseId: string,
  p: number,
  current: number,
  onPageChange: (page: number) => void
): ReactNode {
  const isActive = p === current;
  return (
    <input
      key={`pager_${p}`}
      type="button"
      id={`${baseId}_pager_${p}`}
      className={isActive ? styles.pagerBtnActive : styles.pagerBtnUlm}
      value={String(p)}
      disabled={isActive}
      aria-label={`Page ${p}`}
      aria-current={isActive ? 'page' : undefined}
      onClick={() => {
        if (!isActive) onPageChange(p);
      }}
    />
  );
}

/**
 * Pager: `1` · optional `‹` · window · optional `›` · optional last page (when total &gt; 4).
 * Page 1: `1 2 3 > last` (no `‹`). Pages 2–3: `1 < 2 3 4 > last`. Last: `1 < … last` (no `›`).
 */
export const UgPager = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  visiblePages: visiblePagesProp,
}: UgPagerProps) => {
  const baseId = useId().replace(/:/g, '');
  const total = Math.max(1, totalPages);
  const current = Math.min(Math.max(1, currentPage), total);

  if (total <= 1) {
    return null;
  }

  const nodes: ReactNode[] = [];

  if (total <= 4) {
    for (let p = 1; p <= total; p += 1) {
      nodes.push(pageButton(baseId, p, current, onPageChange));
    }
    return (
      <div
        className={classNames(styles.ugPagerWrap, className)}
        data-testid="ug-pager-wrap"
        role="navigation"
        aria-label="Pagination"
      >
        {nodes}
      </div>
    );
  }

  const win = visiblePagesProp ?? getUgPagerWindowPages(current, total);
  const showLast = win.length > 0 && win[win.length - 1]! < total;
  const showPrev = current > 1;
  const showNext = current < total;

  nodes.push(pageButton(baseId, 1, current, onPageChange));

  if (showPrev) {
    nodes.push(
      <span
        key="pager-prev"
        role="button"
        tabIndex={0}
        className={styles.pagerChevron}
        aria-label="Previous page"
        onClick={() => onPageChange(current - 1)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPageChange(current - 1);
          }
        }}
      >
        ‹
      </span>
    );
  }

  win.forEach((p) => {
    nodes.push(pageButton(baseId, p, current, onPageChange));
  });

  if (showNext) {
    nodes.push(
      <span
        key="pager-next"
        role="button"
        tabIndex={0}
        className={styles.pagerChevron}
        aria-label="Next page"
        onClick={() => onPageChange(current + 1)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPageChange(current + 1);
          }
        }}
      >
        ›
      </span>
    );
  }

  if (showLast) {
    nodes.push(pageButton(baseId, total, current, onPageChange));
  }

  return (
    <div
      className={classNames(styles.ugPagerWrap, className)}
      data-testid="ug-pager-wrap"
      role="navigation"
      aria-label="Pagination"
    >
      {nodes}
    </div>
  );
};
